/**
 * Format a Date object as "YYYY-MM-DD" using the LOCAL timezone.
 *
 * ⚠  Never use `date.toISOString().split('T')[0]` for this — toISOString()
 *    always converts to UTC first, which shifts the date back by 1-2 hours
 *    in timezones ahead of UTC (e.g. UTC+1, UTC+2).
 */
export const toLocalDateStr = (date) => {
    const d = new Date(date);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};


/**
 * RDV (evenement) times — salon wall clock, independent of the browser's
 * timezone and kept identical to what the tablets show.
 *
 * The API stores an RDV as the wall-clock time converted to UTC with PHP's
 * Africa/Casablanca offset (that's what the tablets' sync does), and serves it
 * back as those stored digits labelled with that same offset, e.g. a 18:30
 * RDV comes back as "…T17:30:00+01:00". The tablets rebuild the wall clock as
 * digits + offset, so we do exactly the same here — reading the offset from
 * the value rather than hardcoding it, so this stays in step with the tablets
 * if PHP's timezone data ever changes. (Letting the browser convert it instead
 * shifted every RDV by the difference between the browser's idea of Morocco
 * time — UTC+0 since 20/09/2026 — and PHP's.)
 *
 * Returns a Date whose *local* fields (getHours(), toLocaleTimeString()…) are
 * the salon's wall clock.
 */
export const apiDateToWallClock = (value) => {
    if (typeof value === 'number') return new Date(value * 1000);
    if (typeof value === 'string' && /^\d+$/.test(value)) return new Date(parseInt(value, 10) * 1000);

    const m = typeof value === 'string'
        && value.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?(Z|([+-])(\d{2}):?(\d{2}))?$/);
    if (!m) return new Date(value);

    const [, y, mo, d, h, mi, s = '0', zone, sign, oh, om] = m;
    const offsetMin = !zone || zone === 'Z' ? 0 : (sign === '-' ? -1 : 1) * (Number(oh) * 60 + Number(om));
    const wall = new Date(Date.UTC(+y, +mo - 1, +d, +h, +mi, +s) + offsetMin * 60000);
    return new Date(wall.getUTCFullYear(), wall.getUTCMonth(), wall.getUTCDate(),
        wall.getUTCHours(), wall.getUTCMinutes(), wall.getUTCSeconds());
};

/**
 * Salon wall clock of a Date (its local fields) as "YYYY-MM-DDTHH:mm:ss",
 * without offset: the API reads an offset-less time as Africa/Casablanca wall
 * clock and stores it exactly like the tablets do.
 */
export const wallClockToApi = (date) => {
    const d = new Date(date);
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
};
